import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Globe, Loader2, RefreshCw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

/** SEO Center — missing SEO, internal & broken links, sitemap and canonical
 * validation. Indexing status & backlinks require Google Search Console
 * (not connected yet). Read-only. */

function Card({ label, value, accent = "", children, testid }) {
  return (
    <div className="px-4 py-3 border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <p className={`text-2xl font-serif leading-none ${accent || "text-bone"}`}>{value ?? "—"}</p>
      <p className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-bone/50">{label}</p>
      {children}
    </div>
  );
}

export default function AdminSEOCenter() {
  const [data, setData] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [ov, sc] = await Promise.all([adminApi.seoOverview(), adminApi.seoScanLatest()]);
      setData(ov); setScan(sc);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!scan || !["pending", "running"].includes(scan.status)) return;
    const iv = setInterval(async () => {
      try {
        const s = await adminApi.seoScanLatest();
        setScan(s);
        if (!["pending", "running"].includes(s.status)) clearInterval(iv);
      } catch { clearInterval(iv); }
    }, 3000);
    return () => clearInterval(iv);
  }, [scan?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const startScan = async () => {
    setScanning(true); setErr("");
    try { await adminApi.seoScanStart(); setScan({ status: "running", checked: 0, total: 0, broken: [] }); }
    catch (e) { setErr(e.message); }
    finally { setScanning(false); }
  };

  const canonical = data?.canonical || {};
  const scanRunning = ["pending", "running"].includes(scan?.status);

  return (
    <AdminShell title="SEO Center">
      <p className="text-sm text-bone/60 -mt-6 mb-6 max-w-3xl">
        SEO health of all published content. Read-only — fix issues via the Journal editor or the
        Editorial Dashboard.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="seo-cards">
        <Card label="Published articles" value={data?.published_articles} testid="seo-published" />
        <Card label="Missing SEO" value={data?.missing_seo_count} accent={data?.missing_seo_count ? "text-amber-300" : "text-emerald-300"} testid="seo-missing" />
        <Card label="Internal links" value={data?.internal_links_total} testid="seo-internal" />
        <Card label="Broken internal" value={(data?.broken_internal_links || []).length} accent={(data?.broken_internal_links || []).length ? "text-red-300" : "text-emerald-300"} testid="seo-broken-internal" />
        <Card label="Sitemap URLs" value={data?.sitemap_urls_estimate} testid="seo-sitemap" />
        <Card label="Canonical" value={canonical.ok ? "OK" : "Issues"} accent={canonical.ok ? "text-emerald-300" : "text-red-300"} testid="seo-canonical" />
      </div>

      {/* GSC placeholders */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="seo-gsc">
        {["Indexing status", "Backlinks"].map((l) => (
          <div key={l} className="px-4 py-3 border border-white/10 bg-white/[0.01] flex items-center gap-3">
            <Globe size={15} className="text-bone/30" />
            <div>
              <p className="text-sm text-bone/60">{l}</p>
              <p className="text-[11px] text-bone/35">Requires Google Search Console — not connected yet.</p>
            </div>
          </div>
        ))}
      </div>

      {/* Missing SEO list */}
      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Missing SEO metadata</h2>
      <div className="border border-white/10 bg-white/[0.02] mb-8 divide-y divide-white/5" data-testid="seo-missing-list">
        {(data?.missing_seo || []).length === 0 && <p className="px-4 py-3 text-sm text-bone/40">All articles have SEO metadata.</p>}
        {(data?.missing_seo || []).map((a) => (
          <div key={a.slug} className="flex items-center gap-3 px-4 py-2.5">
            <span className="text-[11px] font-mono text-gold shrink-0">{a.reference || "—"}</span>
            <p className="text-sm text-bone/80 truncate flex-1">{a.title || a.slug}</p>
            <span className="text-[10px] uppercase text-bone/40">{a.status}</span>
            <Link to={`/admin/journal?edit=${encodeURIComponent(a.slug)}`} className="text-gold text-[11px] uppercase tracking-wide hover:text-bone">Fix</Link>
          </div>
        ))}
      </div>

      {/* Broken internal links */}
      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Broken internal links</h2>
      <div className="border border-white/10 bg-white/[0.02] mb-8 divide-y divide-white/5" data-testid="seo-broken-list">
        {(data?.broken_internal_links || []).length === 0 && <p className="px-4 py-3 text-sm text-emerald-300/80">No broken internal links found.</p>}
        {(data?.broken_internal_links || []).map((b, i) => (
          <div key={i} className="px-4 py-2.5 text-sm text-bone/70">
            <span className="text-red-300 font-mono text-[12px]">{b.link}</span> in{" "}
            <Link to={`/admin/journal?edit=${encodeURIComponent(b.slug)}`} className="text-gold hover:text-bone">{b.slug}</Link>
          </div>
        ))}
      </div>

      {/* External link scan */}
      <div className="border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="seo-link-scan">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70">External broken-link scan</h2>
          <button onClick={startScan} disabled={scanning || scanRunning} data-testid="seo-scan-start"
            className="ml-auto inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase border border-gold/40 px-4 py-2 text-gold hover:bg-gold/10 disabled:opacity-40">
            {scanRunning ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {scanRunning ? `Scanning ${scan?.checked ?? 0}/${scan?.total ?? "…"}` : "Run scan"}
          </button>
        </div>
        {scanRunning && (
          <div className="h-1.5 bg-white/5 mb-3" data-testid="seo-scan-progress">
            <div className="h-full bg-gold transition-all" style={{ width: `${scan?.total ? Math.round((scan.checked / scan.total) * 100) : 5}%` }} />
          </div>
        )}
        {scan?.status === "done" && (
          <p className="text-[12px] text-bone/50 mb-2" data-testid="seo-scan-result">
            Last scan: {scan.total} external link(s) checked · {(scan.broken || []).length} broken · {String(scan.finished_at || "").slice(0, 16).replace("T", " ")}
          </p>
        )}
        {(scan?.broken || []).map((b, i) => (
          <p key={i} className="text-[12px] text-red-300 font-mono truncate">
            {b.http_status || "unreachable"} · {b.url} <span className="text-bone/40">({b.slug})</span>
          </p>
        ))}
        {scan?.status === "none" && <p className="text-[12px] text-bone/40">No scans run yet.</p>}
      </div>

      <a href={data?.sitemap_url} target="_blank" rel="noreferrer" data-testid="seo-open-sitemap"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-gold hover:text-bone">
        Open sitemap.xml <ExternalLink size={12} />
      </a>

      {err && <p className="mt-4 text-sm text-red-400" data-testid="seo-error">{err}</p>}
    </AdminShell>
  );
}
